import { Form } from '@/components/form/Form';
import { Property } from '@/components/form/fields/select/SelectInput';
import { Loading } from '@/components/loading/Loading';
import { Multistep } from '@/components/multistep/Multistep';
import { Step } from '@/components/multistep/Questionnaire';
import { Address } from '@/services/api/commonTypes';
import { ContractStatus } from '@/services/api/contracts/partners';
import { useCreatePartnerNda, usePartner, useUpdatePartner } from '@/services/api/hooks/partners';
import { useRegions } from '@/services/api/hooks/region';
import { usePartnerId } from '@/services/authentication/hooks/user';
import { CSVProvider } from '@/services/csv/CSVService';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, Suspense, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import BusinessStep from '../commonSteps/BusinessStep';
import SignTermsAndConditionsStep, { SignerInfo } from '../commonSteps/SignTermsAndConditionsStep';
import { deserialiseAddress, serialiseAddress } from '../commonSteps/addressHelper';
import PartnerOnboardingQuestionnaireTAndCControls from './PartnerOnboardingQuestionaireTermsAndConditonsControls';

interface PartnerOnboardingQuestionnaireProps {}

const PartnerOnboardingQuestionnaireFields = z.object({
  companyInfo: z.object({
    name: z.string(),
    number: z.string(),
    address: Address,
    employeeCount: z.number()
  }),
  region: z.custom<Property>(),
  signerInfo: SignerInfo
});

export type PartnerOnboardingQuestionnaireFields = z.infer<typeof PartnerOnboardingQuestionnaireFields>;

export const PartnerOnboardingQuestionnaire: FC<PartnerOnboardingQuestionnaireProps> = () => {
  const partnerId = usePartnerId();

  if (!partnerId) {
    throw Error('Partner ID must be provided');
  }

  const [partner] = usePartner(partnerId);

  const [regions] = useRegions();

  const createNda = useCreatePartnerNda(partnerId);

  const updatePartner = useUpdatePartner(partnerId);

  const navigate = useNavigate();

  useEffect(() => {
    if (partner.contractStatus !== ContractStatus.Pending && partner.contractStatus !== ContractStatus.Applied) {
      navigate('/');
    }
  }, []);

  const methods = useForm<PartnerOnboardingQuestionnaireFields>({
    resolver: zodResolver(PartnerOnboardingQuestionnaireFields),
    defaultValues: {
      companyInfo: {
        name: partner.registeredInfo.name,
        number: partner.registeredInfo.number,
        address: deserialiseAddress(partner.registeredInfo.address),
        employeeCount: partner.registeredInfo.employeeCount
      },
      region: regions.find(r => r.id === partner.region.id)
    },
    resetOptions: {
      keepErrors: true
    }
  });

  const onSubmit = useCallback(async () => {
    const data = methods.getValues();

    await updatePartner.mutateAsync({
      name: data.companyInfo.name,
      code: partner.code,
      regionId: data.region.id,
      registeredInfo: {
        name: data.companyInfo.name,
        number: data.companyInfo.number,
        address: serialiseAddress(data.companyInfo.address),
        employeeCount: data.companyInfo.employeeCount,
        vatNumber: partner.registeredInfo.vatNumber
      },
      disabled: partner.disabled,
      allowCustomLogo: partner.propertyBag.allowCustomLogo || false
    });

    const result = await createNda.mutateAsync({
      regNumber: data.companyInfo.number,
      regAddress: serialiseAddress(data.companyInfo.address),
      signerEmail: data.signerInfo.email,
      signerName: data.signerInfo.name,
      signerPosition: data.signerInfo.position
    });

    if (result.viewUrl) {
      window.location.replace(result.viewUrl);
    }
  }, []);

  const errorObj = createNda.error as Error | undefined;
  const error = errorObj ? errorObj.message : '';

  return (
    <div className="space-y-3">
      <Form methods={methods} onSubmit={() => {}}>
        <CSVProvider>
          <Multistep onFinish={onSubmit}>
            <Suspense fallback={<Loading />}>
              <Step
                validate={async () => await methods.trigger('companyInfo')}
                key="business-details"
                id="business-details"
                title={'Tell us about your business'}
              >
                <BusinessStep region />
              </Step>
              <Step
                key="sign-terms"
                validate={async () => await methods.trigger('signerInfo')}
                id="sign-terms"
                title={'Sign terms & conditions'}
                controls="manual"
              >
                <SignTermsAndConditionsStep />
                {error && <p className="text-red-500 mt-2">{error}</p>}
                <PartnerOnboardingQuestionnaireTAndCControls />
              </Step>
            </Suspense>
          </Multistep>
        </CSVProvider>
      </Form>
    </div>
  );
};
